import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react"

export interface ElementSize {
  width: number
  height: number
}

/**
 * An element's own rendered content-box size, read via ResizeObserver.
 *
 * `initial` is what callers see until a real measurement lands, and stays in
 * effect whenever the measured rect is exactly 0×0 — jsdom's `getBoundingClientRect`
 * never computes real layout and always reports zero (see `setupTests.ts`'s
 * no-op `ResizeObserver` stub, whose callback never fires either), so this is
 * also what every unit test sees for the life of the render. That makes
 * `initial` the deliberate seam for a duration-based estimate in a grid whose
 * real box size a test can't otherwise see (`estimateEventHeightPx`).
 */
export function useElementSize<T extends HTMLElement = HTMLDivElement>(
  initial: ElementSize
): { ref: RefObject<T | null>; size: ElementSize } {
  const ref = useRef<T>(null)
  const [size, setSize] = useState<ElementSize>(initial)

  const measure = useCallback(() => {
    const node = ref.current
    if (!node) return

    const rect = node.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return

    setSize((current) =>
      current.width === rect.width && current.height === rect.height
        ? current
        : { width: rect.width, height: rect.height }
    )
  }, [])

  useLayoutEffect(measure)

  useEffect(() => {
    if (typeof ResizeObserver === "undefined" || !ref.current) return undefined

    const observer = new ResizeObserver(measure)
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [measure])

  return { ref, size }
}
